import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { CanvasElement, CanvasElementId, WrapperForm } from '@blaze-runtime/engine-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { BaseChildrenHandlerService } from '../../canvas-elements/io-data/default-handlers/base-children-handler.service';
import {
  ComponentChildrenConfig,
  ComponentChildrenHandler,
} from '../../canvas-elements/io-data/io-data.models';
import {
  FormGroupLikeRenderElementInstance,
  InputsCache,
} from '../../canvas-elements/models/render-element.models';

@Injectable({ providedIn: 'root' })
export class FormGroupChildrenHandlerService
  extends BaseChildrenHandlerService
  implements ComponentChildrenHandler
{
  buildInputsCache(element: CanvasElement): InputsCache {
    const inputsCache = Object.entries(getWrapperRefs(element)).reduce(
      (acc, [ref, id]) => {
        if (WrapperForm.isRefId(ref)) {
          acc[id] = WrapperForm.getValue(ref) ?? '';
        }
        return acc;
      },
      {} as Record<CanvasElementId, string | boolean>,
    );
    return inputsCache;
  }

  connectChildren({
    childrenUpdates$,
    inputsCache,
    formInstance,
  }: ComponentChildrenConfig): Observable<void> {
    const instance = formInstance as unknown as FormGroupLikeRenderElementInstance;

    return childrenUpdates$.pipe(
      map(({ event, formElement, inputElement, formContext }) => {
        if (!hasDataProps(inputElement)) {
          return;
        }

        const datasourceRef = inputsCache[event.elementId] as string;
        const dataValueField = this.getDataValueField(inputElement, event.data);

        const { value: inputValue } = this.getChildValue({
          formInstance,
          dataValueField,
          modelField: datasourceRef,
          eventData: event.data,
          formDataBindings: formElement.props.data,
          inputElement,
          instanceValue: instance.value,
          instanceSchema:
            instance.schema || this.getElementOutputSchema(formElement, 'change', formInstance),
          formContext,
        });

        const mainDataSource = getActiveBinding(formElement.props.data, 'value')?.dataSource;
        if (!mainDataSource) {
          throw new Error('form group datasource missing');
        }

        const mainDatasetId = this.schemaService.getDatasetId(
          mainDataSource.id,
          mainDataSource.sourceType,
        );
        if (!mainDatasetId) {
          throw new Error('form group dataset missing');
        }

        if (datasourceRef !== mainDatasetId) {
          return;
        } // prevent loop

        instance.value = inputValue;
        instance.valueUpdated();
        instance.validateValue();
        this.updateDataContainerOutput(formElement, inputElement, instance, event);
      }),
    );
  }
}
